import { z } from 'zod';
import { PointSchema } from './types';
/**
 * Proposal Actions - Atomic operations that can be proposed by agents
 */
export const SpawnTokenActionSchema = z.object({
    type: z.literal('SPAWN_TOKEN'),
    tokenType: z.enum(['character', 'npc', 'object', 'marker']),
    name: z.string(),
    position: PointSchema,
    count: z.number().int().positive().default(1),
    properties: z.object({
        imageUrl: z.string().url().optional(),
        hp: z.number().optional(),
        maxHp: z.number().optional(),
        ac: z.number().optional(),
    }).optional(),
});
export const MoveTokenActionSchema = z.object({
    type: z.literal('MOVE_TOKEN'),
    tokenId: z.string().uuid(),
    to: PointSchema,
    path: z.array(PointSchema).optional(), // For animated movement
});
export const UpdateTokenActionSchema = z.object({
    type: z.literal('UPDATE_TOKEN'),
    tokenId: z.string().uuid(),
    updates: z.object({
        hp: z.number().optional(),
        conditions: z.array(z.string()).optional(),
        visible: z.boolean().optional(),
        locked: z.boolean().optional(),
    }),
});
export const DeleteTokenActionSchema = z.object({
    type: z.literal('DELETE_TOKEN'),
    tokenId: z.string().uuid(),
});
export const RollDiceActionSchema = z.object({
    type: z.literal('ROLL_DICE'),
    expression: z.string(), // e.g., "2d6+3", "1d20"
    reason: z.string().optional(),
    hidden: z.boolean().default(false), // Hidden from players
});
export const UpdateFogActionSchema = z.object({
    type: z.literal('UPDATE_FOG'),
    operation: z.enum(['reveal', 'conceal']),
    area: z.object({
        x: z.number(),
        y: z.number(),
        width: z.number(),
        height: z.number(),
    }),
});
export const PlayAudioActionSchema = z.object({
    type: z.literal('PLAY_AUDIO'),
    url: z.string().url(),
    volume: z.number().min(0).max(1).default(0.5),
    loop: z.boolean().default(false),
    fadeIn: z.number().default(0), // Seconds
});
export const SendMessageActionSchema = z.object({
    type: z.literal('SEND_MESSAGE'),
    content: z.string(),
    recipient: z.enum(['all', 'dm', 'players']).default('all'),
    fromCharacter: z.string().optional(), // Character name for RP
});
// Union of all action types
export const ProposalActionSchema = z.discriminatedUnion('type', [
    SpawnTokenActionSchema,
    MoveTokenActionSchema,
    UpdateTokenActionSchema,
    DeleteTokenActionSchema,
    RollDiceActionSchema,
    UpdateFogActionSchema,
    PlayAudioActionSchema,
    SendMessageActionSchema,
]);
/**
 * Proposal Constraints
 */
export const ProposalConstraintsSchema = z.object({
    requireDMApproval: z.boolean().default(false),
    autoApproveIfDMOffline: z.boolean().default(false),
    expiresAt: z.date().optional(),
    maxRetries: z.number().int().default(0),
    allowPartialApproval: z.boolean().default(false), // Can approve subset of actions
});
/**
 * Proposal Status
 */
export const ProposalStatusSchema = z.enum([
    'pending',
    'approved',
    'partially_approved',
    'rejected',
    'applied',
    'failed',
    'expired',
]);
/**
 * Proposal - Core of the Propose-Approve-Apply model
 */
export const ProposalSchema = z.object({
    id: z.string().uuid(),
    campaignId: z.string().uuid(),
    sceneId: z.string().uuid().optional(), // Scene this proposal affects
    // Source
    source: z.string(), // e.g., "agent:copilot", "agent:media", "user:dm"
    sourceAgentId: z.string().optional(), // If from an agent
    sourceUserId: z.string().optional(), // If from a user
    // Proposal content
    rationale: z.string(), // Why this action is being proposed
    actions: z.array(ProposalActionSchema),
    constraints: ProposalConstraintsSchema.default({}),
    // Status tracking
    status: ProposalStatusSchema.default('pending'),
    approvedBy: z.string().optional(), // User ID who approved
    approvedAt: z.date().optional(),
    appliedAt: z.date().optional(),
    // Partial approval tracking
    approvedActionIndices: z.array(z.number()).optional(),
    // Preview data for UI rendering (optional)
    previewData: z.record(z.unknown()).optional(),
    // Error tracking
    error: z.string().optional(),
    // Metadata
    metadata: z.record(z.unknown()).default({}),
    createdAt: z.date(),
    updatedAt: z.date(),
});
/**
 * API Request/Response types for proposals
 */
export const CreateProposalRequestSchema = z.object({
    source: z.string(),
    rationale: z.string(),
    actions: z.array(ProposalActionSchema),
    constraints: ProposalConstraintsSchema.optional(),
    previewData: z.record(z.unknown()).optional(),
});
export const ApproveProposalRequestSchema = z.object({
    approvedActionIndices: z.array(z.number()).optional(), // For partial approval
});
export const ProposalResponseSchema = ProposalSchema;
//# sourceMappingURL=proposals.js.map